/**
 * BL-1287 (ADR 033 Decision 7) — the CLIENT-SAFE half of the lot transition
 * catalog: the operator-facing metadata every consumer reads, carrying no
 * server code whatsoever.
 *
 * ADR 033 splits `TransitionDef` along a client/server seam. This module is the
 * client side — pure data, importable from a browser bundle (enforced by
 * `__tests__/client-safe-barrel.test.ts`). The server side (`guard` reference,
 * `inTx` / `postCommit` effects) lives in `apps/admin-web/src/lib/lifecycle/`
 * and never transits here.
 *
 * **Why `operatorLabel` lives here.** It is what stops transition ids leaking to
 * the screen. Without it every consumer hand-writes "Withdraw" next to
 * `'withdraw'` and the two drift the first time someone rewords one. The status
 * dropdown, the bulk bar, and BL-1301's in-app reference page all read this.
 *
 * **`from` / `to` are cross-checked, not transcribed.**
 * `__tests__/lot-transition-catalog.test.ts` replays every `live` entry through
 * `canApplyLotTransition` across the whole phase space; a catalog that drifts
 * from the guard fails there. `from` therefore encodes **what the guard decides
 * today**, which is the honest client-side answer — see the two pending-matrix
 * notes below where today's code and the signed matrix have not yet converged.
 *
 * Source of truth for the values: `docs/feature/admin/lifecycle-transition-matrix.md` §3.
 */
import type { LotTransition } from './lot-transition-guard';
import type { LotPhaseKind } from './phase-tokens';

/**
 * Whether the guard decides this transition today. `planned` rows are declared
 * so the catalog stays a TOTAL `Record<LotTransition, …>` — adding a rung to
 * {@link LotTransition} is then a compile error rather than a silent gap — and
 * so BL-1301's reference page can render an unbuilt row honestly instead of
 * omitting it.
 */
export type TransitionStatus = 'live' | 'planned';

/** The operator-facing, client-safe description of one lot lifecycle transition. */
export interface LotTransitionMeta {
  /** The catalog id. Matches this entry's key in {@link LOT_TRANSITION_CATALOG}. */
  readonly id: LotTransition;
  /**
   * The operator-facing name. THE only sanctioned source of this copy — a
   * consumer hard-coding a label next to a transition id fails the AC 7 static
   * check (`apps/admin-web/src/__tests__/transition-label-sources.test.ts`).
   */
  readonly operatorLabel: string;
  /** Whether the guard decides this transition today. */
  readonly status: TransitionStatus;
  /** Source phases the transition may be applied from. Empty for `planned` rows. */
  readonly from: readonly LotPhaseKind[];
  /**
   * Target phase(s). An ARRAY rather than a single phase because `reopen` and
   * `dispose` resolve to one of several targets chosen by the operator — see
   * matrix §3.8 ("`reopen` takes a target phase") and §3.6's terminal rule.
   */
  readonly to: readonly LotPhaseKind[];
  /** True when the transition needs operator input before it can run (opens a form). */
  readonly needsForm: boolean;
  /**
   * The source phases from which this row launches an operation the transition
   * catalog does NOT own (matrix §2, row type 3 — "launches a fenced process").
   * Empty ⇒ never fenced.
   *
   * The one fenced operation is Reverse Sale, deliberately absent from
   * {@link LotTransition} because it carries financial teardown and keeps its
   * own guarded service (`hammer-price-sot.md`). The dropdown is still its entry
   * point, so the catalog has to say WHERE the hand-off happens.
   *
   * **Why a phase list and not a boolean** (BL-1428, M4). This shipped in
   * BL-1287 as `isFenced: boolean` and was never set on any entry — it could not
   * be, because fencing is per-(SOURCE, target), not per-entry. From `sold`
   * every row launches Reverse Sale (matrix §3.0's terminal grid, §5); the very
   * same transitions are perfectly ordinary from every other source. A single
   * boolean would have had to choose between fencing a transition everywhere or
   * nowhere, and both are wrong.
   *
   * INVARIANT: `fencedFrom` and {@link from} never overlap. A source either runs
   * this transition or hands off to the fenced operation — never both. Enforced
   * by `__tests__/lot-transition-catalog.test.ts`.
   */
  readonly fencedFrom: readonly LotPhaseKind[];
}

/**
 * Every lot lifecycle transition. TOTAL record — a new {@link LotTransition}
 * fails to compile until it is declared here AND classified in the test's
 * `EXPECTED_STATUS` table.
 */
export const LOT_TRANSITION_CATALOG: Record<LotTransition, LotTransitionMeta> = {
  // ── live: decided by `canApplyLotTransition` today ────────────────────────
  withdraw: {
    id: 'withdraw',
    operatorLabel: 'Withdraw',
    status: 'live',
    // Matrix §3.5 "Changed 2026-08-18 (a)": every non-terminal phase, including
    // `draft` and `ready`. BL-1289 broadened the guard to match — *Withdrawn* is
    // a RECORD that a consignor pulled an item, which deleting the lot destroys.
    from: [
      'draft',
      'ready',
      'pre-bidding',
      'open-for-bidding',
      'awaiting-resolution',
      'last-call',
      'paused',
    ],
    to: ['withdrawn'],
    needsForm: false,
    fencedFrom: [],
  },
  reopen: {
    id: 'reopen',
    operatorLabel: 'Reopen',
    status: 'live',
    // Every terminal phase EXCEPT `deleted` (a soft-deleted lot is *restored*,
    // not reopened) — matrix §3.8. BL-1387 PR 2 removed `'passed'`: the phase
    // token is retired, so a reserve-not-met lot is `unsold` and is already
    // covered by the entry above it.
    from: ['sold', 'unsold', 'withdrawn'],
    // Matrix §3.8: reopen carries an operator-chosen target.
    to: ['draft', 'ready', 'pre-bidding', 'open-for-bidding'],
    needsForm: true,
    fencedFrom: [],
  },
  'open-for-bidding': {
    id: 'open-for-bidding',
    // BL-1565: the VERB says what the operator is about to start doing, matching
    // the phase it lands in ("Accepting bids"). The id, `from`/`to`/`fencedFrom`
    // cells and every stamp are untouched — this is copy, and ADR 033 forbids a
    // copy edit from re-sequencing or re-naming a transition.
    operatorLabel: 'Accept bids',
    status: 'live',
    // BL-1428 (M2, matrix §3.4): `draft` is a COMPOSITE source — the open stamps
    // `readied_at` and `bidding_opened_at` in one UPDATE, so the lot passes
    // through `ready` without resting there (I2 intact).
    from: ['ready', 'pre-bidding', 'draft'],
    to: ['open-for-bidding'],
    needsForm: false,
    // M4: from `sold` this row launches Reverse Sale instead of running
    // (matrix §3.0's terminal grid + §5). Never overlaps `from`.
    fencedFrom: ['sold'],
  },
  'open-for-pre-bidding': {
    id: 'open-for-pre-bidding',
    // BL-1565 — see the sibling above.
    operatorLabel: 'Accept pre-bids',
    status: 'live',
    // BL-1428 (M2, matrix §3.3): same composite as `open-for-bidding` above —
    // `stampLotPreBidding` COALESCEs `readied_at` alongside `pre_bidding_at`.
    from: ['ready', 'draft'],
    to: ['pre-bidding'],
    needsForm: false,
    // M4: from `sold` this row launches Reverse Sale instead of running
    // (matrix §3.0's terminal grid + §5). Never overlaps `from`.
    fencedFrom: ['sold'],
  },

  'mark-ready': {
    id: 'mark-ready',
    operatorLabel: 'Mark ready',
    status: 'live',
    // Matrix §3.2 — `draft` only. Invariant I2 (no operator-set `ready` inside a
    // live auction) is an auction-phase precondition the service owns, so it is
    // not on this phase axis.
    from: ['draft'],
    to: ['ready'],
    needsForm: false,
    // M4: from `sold` this row launches Reverse Sale instead of running
    // (matrix §3.0's terminal grid + §5). Never overlaps `from`.
    fencedFrom: ['sold'],
  },
  'mark-draft': {
    id: 'mark-draft',
    operatorLabel: 'Move back to draft',
    status: 'live',
    // Matrix §3.1 — every non-terminal phase except `draft` itself.
    from: ['ready', 'pre-bidding', 'open-for-bidding', 'awaiting-resolution', 'last-call', 'paused'],
    to: ['draft'],
    needsForm: false,
    // M4: from `sold` this row launches Reverse Sale instead of running
    // (matrix §3.0's terminal grid + §5). Never overlaps `from`.
    fencedFrom: ['sold'],
  },
  restore: {
    id: 'restore',
    operatorLabel: 'Restore',
    status: 'live',
    // Matrix §3.10 — `deleted` only, and the operator chooses where it lands,
    // which is why this one needs a form. The lot mirror of the auction's
    // `restore`; the rung was added to `LotTransition` in BL-1289 under the
    // 2026-08-20 lifecycle-change sign-off.
    from: ['deleted'],
    to: ['draft', 'ready', 'pre-bidding', 'open-for-bidding'],
    needsForm: true,
    fencedFrom: [],
  },

  // ── planned: the guard throws TransitionNotImplementedError ───────────────
  // `from` stays EMPTY on purpose. The guard has no opinion yet, so any source
  // set written here would be an unverifiable claim the cross-check test cannot
  // police. `to` records the matrix's stated destination, which is stable.
  // Both remaining rows are the FENCED tier — `dispose` and `close` carry hammer
  // price into the invoice pipeline (matrix §3.6/§3.7), so they are deliberately
  // NOT part of BL-1289's pure-lifecycle migration.
  dispose: {
    id: 'dispose',
    // Matrix §3.6 — the clerk's "what happened to this lot" result entry.
    operatorLabel: 'Record result',
    // BL-1296: live. `from` mirrors the guard exactly — matrix §3.6 allows the
    // live phases plus every terminal EXCEPT `sold`, because leaving a sale
    // carries financial teardown and keeps Reverse Sale.
    status: 'live',
    // BL-1428 (M3, matrix §3.6): the pre-open phases were added. A result can be
    // recorded on a lot that never opened — operators record sales handled
    // off-platform. Every phase except `sold` (Reverse Sale) and `deleted`.
    from: [
      'draft',
      'ready',
      'pre-bidding',
      'open-for-bidding',
      'last-call',
      'awaiting-resolution',
      'paused',
      'unsold',
      'withdrawn',
    ],
    // §3.6's terminal rule: any terminal disposition except leaving `sold`.
    //
    // BL-1387 PR 1: `'passed'` was removed. `dispose` writes the DISPOSITION
    // `'passed'` (a reserve-not-met result) but lands the lot in the **`unsold`**
    // PHASE — `determineDisposition` returns `newStatus: 'sold' | 'unsold'` and
    // nothing else. The `passed` phase has no writer anywhere in production, so
    // naming it here promised a destination the transition cannot reach.
    to: ['sold', 'unsold', 'withdrawn'],
    needsForm: true,
    // M4: from `sold` this row launches Reverse Sale instead of running
    // (matrix §3.0's terminal grid + §5). Never overlaps `from`.
    fencedFrom: ['sold'],
  },
  close: {
    id: 'close',
    operatorLabel: 'Close',
    // BL-1296: live. Matrix §3.7 — only the two phases whose close time has
    // already passed; everything else is `not-closeable`.
    status: 'live',
    from: ['awaiting-resolution', 'last-call'],
    // §3.7 — the timed close resolves the lot to whichever disposition applies.
    //
    // BL-1387 PR 1: `'passed'` was removed, for the same reason as `dispose`
    // above — the close freezes a `sold` or `unsold` phase, never a `passed` one.
    to: ['sold', 'unsold'],
    needsForm: false,
    fencedFrom: [],
  },
  'soft-delete': {
    id: 'soft-delete',
    operatorLabel: 'Delete',
    status: 'live',
    // Matrix §3.9 as AMENDED by BL-1431 (operator sign-off 2026-08-31): every
    // phase except `deleted` and `sold`. The non-sold terminals stay — a lot that
    // closed WITHOUT selling owes nothing, so deleting it is a clerical action.
    //
    // `sold` was here until BL-1431 and is now fenced: leaving a sale carries
    // financial teardown that Reverse Sale owns, which is already how every
    // OTHER row on a sold lot behaves (M4). Mirrors the guard's branch in
    // `lot-transition-guard.ts` — keep the two in step.
    //
    // The `SoftDeleteService` restrict/guard blockers (ADR 018) are a live-data
    // precondition, not a phase, so they are not encoded here.
    from: [
      'draft',
      'ready',
      'pre-bidding',
      'open-for-bidding',
      'awaiting-resolution',
      'last-call',
      'paused',
      'withdrawn',
      'unsold',
    ],
    to: ['deleted'],
    needsForm: false,
    fencedFrom: [],
  },
};

/**
 * The transitions the guard decides today, for consumers that must not offer an
 * unbuilt row (the status dropdown, the bulk bar). BL-1301's reference page
 * reads the full {@link LOT_TRANSITION_CATALOG} instead.
 */
export const LIVE_LOT_TRANSITIONS: readonly LotTransitionMeta[] = Object.values(
  LOT_TRANSITION_CATALOG,
).filter((meta) => meta.status === 'live');
